<script setup lang="ts">
import { computed } from 'vue';
import {
  Document,
  Microphone,
  Picture,
  Promotion,
  Scissor,
  Search,
  Clock,
  User,
} from '@element-plus/icons-vue';
import { useRouter } from 'vue-router';
import { WORKFLOW_STEPS, type StepKey } from '@luogen/shared';

const props = defineProps<{
  activeStep: StepKey;
  accountLabel: string;
  online: boolean;
}>();

const emit = defineEmits<{ navigate: [key: StepKey]; openLogin: [] }>();
const router = useRouter();

const STEP_ICONS: Record<StepKey, unknown> = {
  topic: Search,
  copywriting: Document,
  audio: Microphone,
  'digital-human': User,
  editing: Scissor,
  cover: Picture,
  publish: Promotion,
};

const items = computed(() =>
  WORKFLOW_STEPS.map((step) => ({ key: step.key, label: step.label, icon: STEP_ICONS[step.key] })),
);
</script>

<template>
  <nav class="rail">
    <button
      v-for="item in items"
      :key="item.key"
      type="button"
      class="rail__item"
      :class="{ 'is-active': item.key === props.activeStep }"
      :title="item.label"
      @click="emit('navigate', item.key)"
    >
      <el-icon :size="17"><component :is="item.icon" /></el-icon>
    </button>

    <span class="rail__spacer" />

    <button class="rail__item" type="button" title="任务中心" @click="router.push('/tasks')">
      <el-icon :size="16"><Clock /></el-icon>
    </button>

    <button class="rail__account" type="button" :title="props.accountLabel" @click="emit('openLogin')">
      <span class="rail__dot" :class="{ 'is-online': props.online }" />
      <el-icon :size="16"><User /></el-icon>
    </button>
  </nav>
</template>

<style scoped>
/* 真实产品：左侧竖向图标轨，当前步黑底白图标，底部账号 */
.rail {
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  width: 68px;
  padding: 76px 0 18px;
  -webkit-app-region: no-drag;
}

.rail__item,
.rail__account {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: 1px solid transparent;
  border-radius: 10px;
  background: transparent;
  color: var(--lg-color-text-muted);
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.rail__item:hover,
.rail__account:hover {
  background: var(--lg-color-surface);
  border-color: var(--lg-color-border);
  color: var(--lg-color-text-primary);
}

.rail__item.is-active {
  background: var(--lg-color-action-primary);
  border-color: var(--lg-color-action-primary);
  color: var(--lg-color-action-primary-text);
}

.rail__spacer {
  flex: 1;
}

.rail__account {
  position: relative;
}

.rail__dot {
  position: absolute;
  top: 3px;
  right: 3px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--lg-color-text-disabled);
}

.rail__dot.is-online {
  background: var(--lg-color-success);
}
</style>
